import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Upload, Trash2, Loader2, Camera, Move, Check, RotateCcw, ZoomIn, ZoomOut,
    Save, Mail, Printer, X, Maximize2,
} from "lucide-react";
import { api } from "../../lib/api/_client";
import { formatApiError } from "../../lib/auth";
import { listEmailAccounts } from "../../lib/api/email";
import { listContacts } from "../../lib/api/contacts";

// iter-168 — Renders the as-built page as an interactive composite:
// shipping template as the background, 3 upload slots (design /
// cross-section / criteria) overlaid on regions #1/#2/#3, and an
// elevations panel overlaid on the title-block-adjacent box. Uploading
// the cross-section triggers server-side Claude vision OCR to fill the
// elevation values automatically. Each cell has upload / replace /
// delete controls; elevations are inline-editable and manual edits
// win over future OCR runs.

// iter-168r — Region rectangles sit INSIDE the template's black
// work-area frame instead of overlapping the outer canvas edges and
// the right-hand title strip. The template.png has ~1.5% margin on
// the left/top/bottom of the drawing area and the title strip starts
// around 78%. Percentages must stay in sync with REGION_RECTS_PCT
// in backend/routes/asbuilt.py.
const REGION_LAYOUT = {
    design:        { left: "1.5%", top: "1%",   width: "76%",   height: "43.5%",
                     label: "DESIGN",        cellNo: "#1", ideal: "1520 × 670 px" },
    cross_section: { left: "1.5%", top: "45.5%", width: "37%",   height: "48.5%",
                     label: "CROSS SECTION", cellNo: "#2", ideal: "740 × 750 px" },
    criteria:      { left: "39.5%", top: "45.5%", width: "38%",   height: "48.5%",
                     label: "CRITERIA",      cellNo: "#3", ideal: "760 × 750 px" },
};

// Pixel-measured from the 2000x1545 template: the baked "ELEVATIONS:
// SEPTIC SYSTEM" box has label baselines at y=603/628/653/679/704 and
// its inner right edge at x=1971. Inputs sit right-aligned in that
// box; the server stamps the same values at the same spots on compose.
const ELEV_VALUE_BOX = { left: "91.5%", width: "6.6%", height: `${(20 / 1545) * 100}%` };
const ELEVATION_ROWS = [
    { key: "base_cut",           label: "Base cut",           baseline: 603 },
    { key: "top_of_filter_sand", label: "Top of filter sand", baseline: 628 },
    { key: "top_of_gsf",         label: "Top of GSF",         baseline: 653 },
    { key: "bottom_of_gsf",      label: "Bottom of GSF",      baseline: 679 },
    { key: "bottom_of_gsf_sand", label: "Bottom of GSF Sand", baseline: 704 },
].map((r) => ({ ...r, top: `${((r.baseline - 16) / 1545) * 100}%` }));

export default function AsBuiltCanvas({ activeFile, backendUrl, token, project, onUpdated }) {
    const [busyRegion, setBusyRegion] = useState(null);
    const [ocrBusy, setOcrBusy] = useState(false);
    const [elev, setElev] = useState(activeFile?.asbuilt_fields?.elevations || {});
    // Bust image cache after re-upload — we keep the same storage key
    // per region but the underlying blob changed.
    const [bust, setBust] = useState(0);
    const inputsRef = useRef({});

    // iter-168g — Pan + pinch-zoom viewport. Outer wrapper is a native
    // scroll container (touch-action pan-x pan-y) so 1-finger drags
    // pan naturally on mobile and desktops get scrollbars. Pinch
    // multiplies `viewScale`; content scales by increasing the inner
    // container's width beyond 100%. Fit-to-window on load = scale 1
    // (already fits the outer container's width by design). We expose
    // ± / Fit controls next to Save/Email/Print. Pan/pinch stay dormant
    // while the user is inside a per-region crop gesture so the
    // existing in-box crop tool keeps working.
    const [viewScale, setViewScale] = useState(1);
    const viewportRef = useRef(null);
    const viewPinchRef = useRef(null);
    const viewPanRef = useRef(null);
    const [isPanning, setIsPanning] = useState(false);
    const MIN_ZOOM = 0.25;
    const MAX_ZOOM = 4;
    const ZOOM_STEP = 0.25;
    const clampZoom = (n) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, n));
    const zoomBy = (d) => setViewScale((s) => clampZoom(Math.round((s + d) * 100) / 100));
    const fitView = () => {
        setViewScale(1);
        if (viewportRef.current) {
            viewportRef.current.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
    };

    // iter-168c — In-box crop tool. One region can be in "edit" mode
    // at a time; while it is, the image renders with object-cover +
    // CSS transform (scale, translate) that the user drags. On save we
    // PATCH the transform to the server.
    const [cropRegion, setCropRegion] = useState(null);
    const [cropDraft, setCropDraft] = useState(null); // {scale, offset_x, offset_y}
    const dragRef = useRef(null);

    // iter-168o — Native wheel handler for desktop zoom. React's
    // onWheel is passive so preventDefault is ignored; attach with
    // `{ passive: false }` here. Ctrl/Cmd + wheel zooms, plain wheel
    // falls through to native scroll.
    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        const handler = (e) => {
            if (cropRegion) return;
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const step = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
                setViewScale((s) => clampZoom(Math.round((s + step) * 100) / 100));
            }
        };
        el.addEventListener("wheel", handler, { passive: false });
        return () => el.removeEventListener("wheel", handler);
    }, [cropRegion]);

    // iter-168o — Desktop click-drag to pan the canvas inside the
    // viewport. Skips buttons/inputs so clicks on Adjust / Replace /
    // Save PNG etc still register. Skips while `cropRegion` is set so
    // the in-region drag-to-reposition tool owns the pointer.
    const onViewMouseDown = (e) => {
        if (cropRegion) return;
        if (e.button !== 0) return;
        if (e.target?.closest?.("button, input, textarea, a, label, [contenteditable]")) return;
        if (!viewportRef.current) return;
        viewPanRef.current = {
            x: e.clientX,
            y: e.clientY,
            sx: viewportRef.current.scrollLeft,
            sy: viewportRef.current.scrollTop,
        };
        setIsPanning(true);
        e.preventDefault();
    };
    const onViewMouseMove = (e) => {
        if (!viewPanRef.current || !viewportRef.current) return;
        viewportRef.current.scrollLeft = viewPanRef.current.sx - (e.clientX - viewPanRef.current.x);
        viewportRef.current.scrollTop = viewPanRef.current.sy - (e.clientY - viewPanRef.current.y);
    };
    const onViewMouseUp = () => {
        viewPanRef.current = null;
        setIsPanning(false);
    };

    const startCrop = (name) => {
        const existing = activeFile?.asbuilt_fields?.regions?.[name]?.transform || {};
        setCropRegion(name);
        setCropDraft({
            scale: Number(existing.scale) || 1.0,
            offset_x: Number(existing.offset_x) || 0,
            offset_y: Number(existing.offset_y) || 0,
        });
    };
    const cancelCrop = () => { setCropRegion(null); setCropDraft(null); };
    const bumpZoom = (delta) => {
        setCropDraft((d) => {
            if (!d) return d;
            const next = Math.min(4, Math.max(1, (d.scale || 1) + delta));
            return { ...d, scale: next };
        });
    };
    const resetCrop = () => setCropDraft({ scale: 1, offset_x: 0, offset_y: 0 });
    const saveCrop = async () => {
        if (!cropRegion || !cropDraft) return;
        try {
            const { data } = await api.patch(`/asbuilt/${activeFile.id}`, {
                region_transforms: { [cropRegion]: cropDraft },
            });
            onUpdated && onUpdated(data);
            toast.success("Crop saved");
        } catch (err) {
            toast.error(formatApiError(err) || "Save failed");
        } finally {
            cancelCrop();
        }
    };
    const onDragStart = (e) => {
        if (!cropRegion) return;
        e.preventDefault?.();
        e.stopPropagation?.();
        const pt = "touches" in e ? e.touches[0] : e;
        dragRef.current = { x: pt.clientX, y: pt.clientY, base: { ...cropDraft } };
    };
    const onDragMove = (e) => {
        if (!dragRef.current || !cropRegion) return;
        const pt = "touches" in e ? e.touches[0] : e;
        const el = e.currentTarget;
        const rect = el.getBoundingClientRect();
        // Convert pixel delta → percent of the box so crops are stable
        // across viewport sizes.
        const dx = ((pt.clientX - dragRef.current.x) / rect.width) * 100;
        const dy = ((pt.clientY - dragRef.current.y) / rect.height) * 100;
        setCropDraft((d) => ({
            ...d,
            offset_x: dragRef.current.base.offset_x + dx,
            offset_y: dragRef.current.base.offset_y + dy,
        }));
    };
    const onDragEnd = () => { dragRef.current = null; };

    // iter-168e — Pinch-to-zoom on tablets. When two fingers are on
    // the region while in crop mode, we track the distance between
    // them and scale the image proportionally. Single-finger drag
    // (onDragStart / onDragMove) still works. onGesture* wrappers
    // dispatch between the two based on touch count.
    const pinchRef = useRef(null);
    const onGestureStart = (e) => {
        if (!cropRegion) return;
        if (e.touches && e.touches.length >= 2) {
            e.preventDefault();
            const t1 = e.touches[0], t2 = e.touches[1];
            pinchRef.current = {
                dist: Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY),
                baseScale: (cropDraft && cropDraft.scale) || 1,
            };
            dragRef.current = null;
            return;
        }
        onDragStart(e);
    };
    const onGestureMove = (e) => {
        if (!cropRegion) return;
        if (e.touches && e.touches.length >= 2 && pinchRef.current) {
            e.preventDefault();
            const t1 = e.touches[0], t2 = e.touches[1];
            const d = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
            const ratio = d / (pinchRef.current.dist || 1);
            const next = Math.min(4, Math.max(1, pinchRef.current.baseScale * ratio));
            setCropDraft((cd) => ({ ...cd, scale: next }));
            return;
        }
        onDragMove(e);
    };
    const onGestureEnd = (e) => {
        pinchRef.current = null;
        onDragEnd(e);
    };

    // iter-168d — Save / Email / Print state.
    const [saveBusy, setSaveBusy] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailBusy, setEmailBusy] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [linkedContact, setLinkedContact] = useState(null);
    const [emailForm, setEmailForm] = useState({ account_id: "", to: "", subject: "", body: "" });

    // Fetch email accounts + linked contact lazily.
    useEffect(() => {
        (async () => {
            try {
                const rows = await listEmailAccounts();
                setAccounts(rows || []);
            } catch { /* silent */ }
        })();
    }, []);
    useEffect(() => {
        if (!project?.contact_id) { setLinkedContact(null); return; }
        (async () => {
            try {
                const rows = await listContacts();
                const c = (rows || []).find((r) => r.id === project.contact_id);
                setLinkedContact(c || null);
            } catch { /* silent */ }
        })();
    }, [project?.contact_id]);

    const openEmailDialog = () => {
        // iter-169f — use the project's STREET (from the structured
        // address; fall back to legacy location's first line or the
        // project name) in both subject and body so the email reads
        // like a real jobsite reference: "2802 Dempster As-Built
        // ST-001 — Kalvin Seilis".
        const projectStreet =
            project?.street_address
            || String(project?.location || "").split(",")[0].trim()
            || project?.name
            || "your project";
        const contactFullName = linkedContact?.name || "";
        const firstName = contactFullName.split(" ")[0] || "";
        const drawingNo = activeFile?.asbuilt_fields?.drawing_no || "";
        setEmailForm({
            account_id: accounts[0]?.id || "",
            to: linkedContact?.email || "",
            subject: `${projectStreet} As-Built ${drawingNo}${contactFullName ? ` — ${contactFullName}` : ""}`.trim(),
            body: `Hi${firstName ? ` ${firstName}` : ""},\n\nPlease find the attached as-built drawing for ${projectStreet}.\n\nThanks,\nSteeltooth Contracting`,
        });
        setEmailOpen(true);
    };

    const downloadSave = async () => {
        setSaveBusy(true);
        try {
            // Regenerate on save so the latest state (transforms +
            // elevations) is baked into the PNG.
            await api.post(`/asbuilt/${activeFile.id}/compose?force=1`);
            const url = `${backendUrl}/api/estimating/asbuilt/${activeFile.id}/composed.png${
                token ? `?token=${encodeURIComponent(token)}` : ""
            }`;
            const res = await api.get(`/asbuilt/${activeFile.id}/composed.png`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = activeFile.original_filename || "as-built.png";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
            toast.success("Saved");
        } catch (err) {
            toast.error(formatApiError(err) || "Save failed");
        } finally {
            setSaveBusy(false);
        }
    };

    const printAsBuilt = async () => {
        try {
            await api.post(`/asbuilt/${activeFile.id}/compose?force=1`);
            const res = await api.get(`/asbuilt/${activeFile.id}/composed.png`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const w = window.open("", "_blank");
            if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
            w.document.write(`<html><head><title>${activeFile.original_filename || "As-Built"}</title></head><body style="margin:0"><img src="${url}" style="max-width:100%;height:auto"/><script>window.onload=()=>{setTimeout(()=>{window.print();},400);}<\/script></body></html>`);
            w.document.close();
        } catch (err) {
            toast.error(formatApiError(err) || "Print failed");
        }
    };

    const sendEmail = async () => {
        if (!emailForm.account_id || !emailForm.to.trim()) {
            toast.error("Pick a from-address and enter a recipient");
            return;
        }
        setEmailBusy(true);
        try {
            await api.post(`/asbuilt/${activeFile.id}/compose?force=1`);
            const to = emailForm.to.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
            await api.post(`/asbuilt/${activeFile.id}/email`, {
                account_id: emailForm.account_id,
                to,
                subject: emailForm.subject,
                body: emailForm.body,
            });
            toast.success("Email sent");
            setEmailOpen(false);
        } catch (err) {
            toast.error(formatApiError(err) || "Send failed");
        } finally {
            setEmailBusy(false);
        }
    };

    // iter-168x — Mirror the server-side auto-inference here so the
    // editor card always shows 5 rows (Top of filter sand == Bottom
    // of GSF in a GSF/sand-filter design when OCR misses the callout).
    const elevDisplay = useMemo(() => {
        const e = { ...(elev || {}) };
        if (!(e.top_of_filter_sand || "").trim()) {
            const b = (e.bottom_of_gsf || "").trim();
            if (b) e.top_of_filter_sand = b;
        }
        return e;
    }, [elev]);
    useEffect(() => {
        setElev(activeFile?.asbuilt_fields?.elevations || {});
    }, [activeFile?.asbuilt_fields?.elevations, activeFile?.id]);

    // iter-168f — Debounced auto-recompose. Whenever the persisted
    // as-built state changes (regions swapped, transforms saved,
    // elevations edited), regenerate the composite PNG ~800ms after
    // the last change so Save / Email / Print always pick up the
    // freshest bake without users needing to hit a button. We skip
    // while a crop is being dragged so we don't spam the endpoint,
    // and we hash the signature so unrelated re-renders don't fire.
    const composeSignature = useMemo(() => {
        const f = activeFile?.asbuilt_fields || {};
        const regions = f.regions || {};
        const shape = {};
        for (const [k, v] of Object.entries(regions)) {
            if (!v) { shape[k] = null; continue; }
            shape[k] = {
                sp: v.storage_path || null,
                t: v.transform || null,
            };
        }
        return JSON.stringify({ r: shape, e: f.elevations || {} });
    }, [activeFile?.asbuilt_fields]);

    const lastComposedSigRef = useRef(null);
    useEffect(() => {
        if (!activeFile?.id || !activeFile?.is_asbuilt) return;
        if (cropRegion) return; // don't recompose mid-drag
        if (lastComposedSigRef.current === composeSignature) return;
        const t = setTimeout(async () => {
            try {
                await api.post(`/asbuilt/${activeFile.id}/compose?force=1`);
                lastComposedSigRef.current = composeSignature;
            } catch { /* silent — Save/Email/Print force-recompose anyway */ }
        }, 800);
        return () => clearTimeout(t);
    }, [composeSignature, activeFile?.id, activeFile?.is_asbuilt, cropRegion]);

    if (!activeFile?.is_asbuilt) return null;

    const templateUrl = `${backendUrl}/api/estimating/asbuilt/${activeFile.id}/template.png${
        token ? `?token=${encodeURIComponent(token)}` : ""
    }`;
    const regions = activeFile.asbuilt_fields?.regions || {};

    const regionImageUrl = (name) => (
        `${backendUrl}/api/estimating/asbuilt/${activeFile.id}/region/${name}/image${
            token ? `?token=${encodeURIComponent(token)}` : ""
        }&v=${bust}`
    );

    const uploadRegion = async (name, file) => {
        if (!file) return;
        setBusyRegion(name);
        if (name === "cross_section") setOcrBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(
                `/asbuilt/${activeFile.id}/region/${name}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setBust((v) => v + 1);
            onUpdated && onUpdated(data);
            if (name === "cross_section") {
                const e = data?.asbuilt_fields?.elevations || {};
                const filled = Object.values(e).some((v) => v && String(v).trim());
                toast.success(filled
                    ? "Cross-section uploaded — elevations filled from image"
                    : "Cross-section uploaded (couldn't read elevations)");
            } else {
                toast.success("Uploaded");
            }
        } catch (err) {
            toast.error(formatApiError(err) || "Upload failed");
        } finally {
            setBusyRegion(null);
            setOcrBusy(false);
        }
    };

    const deleteRegion = async (name) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete the ${REGION_LAYOUT[name].label.toLowerCase()} image?`)) return;
        setBusyRegion(name);
        try {
            const { data } = await api.delete(`/asbuilt/${activeFile.id}/region/${name}`);
            setBust((v) => v + 1);
            onUpdated && onUpdated(data);
        } catch (err) {
            toast.error(formatApiError(err) || "Delete failed");
        } finally {
            setBusyRegion(null);
        }
    };

    const saveElevations = async () => {
        try {
            const { data } = await api.patch(`/asbuilt/${activeFile.id}`, { elevations: elev });
            onUpdated && onUpdated(data);
            toast.success("Elevations saved");
        } catch (err) {
            toast.error(formatApiError(err) || "Save failed");
        }
    };

    return (
        <div className="relative w-full flex-1 min-h-0 flex flex-col" data-testid="asbuilt-canvas">
            {/* iter-168d — Save / Email / Print toolbar */}
            <div className="shrink-0 flex items-center justify-between gap-2 py-2 px-2 flex-wrap" data-testid="asbuilt-toolbar">
                {/* iter-168g — Zoom controls. Hidden inside crop mode so
                    the per-region gesture is never fighting the outer
                    pinch. Fit resets to scale=1 (fit-to-window). */}
                <div
                    className="inline-flex items-stretch rounded overflow-hidden border border-slate-300 bg-white shadow-sm"
                    data-testid="asbuilt-zoom-controls"
                >
                    <button
                        type="button"
                        onClick={() => zoomBy(-ZOOM_STEP)}
                        disabled={!!cropRegion || viewScale <= MIN_ZOOM}
                        data-testid="asbuilt-zoom-out"
                        title="Zoom out"
                        className="px-2 py-1.5 text-slate-800 hover:bg-slate-100 disabled:opacity-40"
                    >
                        <ZoomOut className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={fitView}
                        disabled={!!cropRegion}
                        data-testid="asbuilt-zoom-fit"
                        title="Fit to window"
                        className="px-2 py-1.5 text-slate-800 hover:bg-slate-100 disabled:opacity-40 text-[11px] font-bold tabular-nums inline-flex items-center gap-1 border-x border-slate-300"
                    >
                        <Maximize2 className="w-3 h-3" />
                        {Math.round(viewScale * 100)}%
                    </button>
                    <button
                        type="button"
                        onClick={() => zoomBy(ZOOM_STEP)}
                        disabled={!!cropRegion || viewScale >= MAX_ZOOM}
                        data-testid="asbuilt-zoom-in"
                        title="Zoom in"
                        className="px-2 py-1.5 text-slate-800 hover:bg-slate-100 disabled:opacity-40"
                    >
                        <ZoomIn className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={downloadSave}
                    disabled={saveBusy}
                    data-testid="asbuilt-save"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-xs font-bold shadow"
                >
                    {saveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save PNG
                </button>
                <button
                    type="button"
                    onClick={openEmailDialog}
                    data-testid="asbuilt-email"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white text-xs font-bold shadow"
                >
                    <Mail className="w-3.5 h-3.5" /> Email
                </button>
                <button
                    type="button"
                    onClick={printAsBuilt}
                    data-testid="asbuilt-print"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-white hover:bg-slate-100 text-slate-900 text-xs font-bold shadow border border-slate-300"
                >
                    <Printer className="w-3.5 h-3.5" /> Print
                </button>
                </div>
            </div>
            {/* iter-168g — Scrollable viewport wraps the canvas so users
                can pan by dragging (native scroll) after zooming in. On
                mobile this also enables a natural "pinch to zoom" via
                the touch handlers below. `touchAction: pan-x pan-y`
                lets 1-finger drags pan freely; we cancel default on 2+
                fingers so pinch scales rather than page-zooms. */}
            <div
                ref={viewportRef}
                className="relative flex-1 min-h-0 overflow-auto bg-slate-50 border-t border-slate-200"
                style={{
                    // Viewport fills whatever height the workspace
                    // gives it (parent is overflow-hidden flex-col), so
                    // its own scrollbar always reaches the canvas bottom.
                    touchAction: cropRegion ? "none" : "pan-x pan-y",
                    WebkitOverflowScrolling: "touch",
                    cursor: cropRegion ? "default" : (isPanning ? "grabbing" : "grab"),
                }}
                onMouseDown={onViewMouseDown}
                onMouseMove={onViewMouseMove}
                onMouseUp={onViewMouseUp}
                onMouseLeave={onViewMouseUp}
                onTouchStart={(e) => {
                    if (cropRegion) return;
                    if (e.touches && e.touches.length >= 2) {
                        const t1 = e.touches[0], t2 = e.touches[1];
                        viewPinchRef.current = {
                            d0: Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY),
                            s0: viewScale,
                        };
                    }
                }}
                onTouchMove={(e) => {
                    if (cropRegion || !viewPinchRef.current) return;
                    if (e.touches && e.touches.length >= 2) {
                        e.preventDefault();
                        const t1 = e.touches[0], t2 = e.touches[1];
                        const d = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                        const next = clampZoom(viewPinchRef.current.s0 * (d / (viewPinchRef.current.d0 || 1)));
                        setViewScale(next);
                    }
                }}
                onTouchEnd={() => { viewPinchRef.current = null; }}
                data-testid="asbuilt-viewport"
            >
                <div
                    className="mx-auto"
                    style={{
                        width: `${100 * viewScale}%`,
                        maxWidth: viewScale <= 1 ? "1500px" : undefined,
                    }}
                >
            <div className="relative w-full" style={{ aspectRatio: "2000 / 1545", containerType: "inline-size" }}>
                <img
                    src={templateUrl}
                    alt="As-Built template"
                    className="absolute inset-0 w-full h-full object-contain bg-white"
                    draggable={false}
                />

                {Object.entries(REGION_LAYOUT).map(([name, box]) => {
                    const filled = !!regions[name];
                    const busy = busyRegion === name;
                    const inCrop = cropRegion === name;
                    const savedT = regions[name]?.transform || { scale: 1, offset_x: 0, offset_y: 0 };
                    const t = inCrop ? cropDraft : savedT;
                    // iter-168i — Default display is object-contain so the
                    // whole uploaded image is visible (letterboxed if the
                    // aspect doesn't match the region box). Only switch
                    // to object-cover + transform when the user is
                    // actively cropping OR has already saved a
                    // non-default crop transform. Matches the user's
                    // expectation that "scale to fit" leaves nothing
                    // clipped.
                    const hasCustomCrop = (savedT.scale || 1) !== 1
                        || (savedT.offset_x || 0) !== 0
                        || (savedT.offset_y || 0) !== 0;
                    const useCoverFit = inCrop || hasCustomCrop;
                    const imgStyle = {
                        objectFit: useCoverFit ? "cover" : "contain",
                        transform: useCoverFit
                            ? `translate(${t.offset_x || 0}%, ${t.offset_y || 0}%) scale(${t.scale || 1})`
                            : "none",
                        transformOrigin: "center center",
                        cursor: inCrop ? "grab" : "default",
                        touchAction: inCrop ? "none" : "auto",
                    };
                    return (
                        <div
                            key={name}
                            className="absolute border-2 border-dashed overflow-hidden"
                            style={{
                                left: box.left, top: box.top,
                                width: box.width, height: box.height,
                                borderColor: inCrop ? "rgba(0,163,255,1)" : (filled ? "rgba(0,163,255,0.25)" : "rgba(0,163,255,0.75)"),
                                background: filled ? "rgba(255,255,255,0)" : "rgba(255,255,255,0.55)",
                                // iter-168n/o — Kill native pan-scroll on
                                // this box while cropping so touchmove
                                // events reach our drag handler, and
                                // signal draggability on desktop with a
                                // grab cursor.
                                touchAction: inCrop ? "none" : "auto",
                                cursor: inCrop ? (dragRef.current ? "grabbing" : "grab") : "default",
                                userSelect: inCrop ? "none" : "auto",
                            }}
                            data-testid={`asbuilt-region-${name}`}
                            onMouseDown={inCrop ? onDragStart : undefined}
                            onMouseMove={inCrop ? onDragMove : undefined}
                            onMouseUp={inCrop ? onDragEnd : undefined}
                            onMouseLeave={inCrop ? onDragEnd : undefined}
                            onTouchStart={inCrop ? onGestureStart : undefined}
                            onTouchMove={inCrop ? onGestureMove : undefined}
                            onTouchEnd={inCrop ? onGestureEnd : undefined}
                        >
                            {filled ? (
                                <img
                                    src={regionImageUrl(name)}
                                    alt={box.label}
                                    className="absolute inset-0 w-full h-full select-none pointer-events-none"
                                    style={imgStyle}
                                    draggable={false}
                                    data-testid={`asbuilt-region-${name}-image`}
                                />
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-800 px-3 text-center overflow-hidden">
                                    <div className="text-5xl sm:text-6xl md:text-7xl font-black opacity-30 leading-none">
                                        {box.cellNo}
                                    </div>
                                    <div className="text-sm sm:text-base md:text-lg font-bold uppercase tracking-widest">
                                        {box.label}
                                    </div>
                                    <div className="text-[10px] sm:text-xs text-slate-600 mt-1 font-medium">
                                        Ideal: <span className="tabular-nums">{box.ideal}</span>
                                    </div>
                                    <div className="hidden md:block text-[10px] text-slate-500 italic max-w-[80%] leading-tight">
                                        Any size works — off-size images will be scaled to fit this box.
                                    </div>
                                </div>
                            )}

                            {/* iter-174 — Upload/Replace button lives
                                at the top-right of each region so it
                                stops covering the "Ideal: WxH" copy
                                and the "#N Section" label at the
                                bottom. Other action buttons (Adjust,
                                Delete, crop controls) stay bottom-right
                                because they only appear once a photo
                                is filled and no longer overlap text. */}
                            {!inCrop && (
                                <button
                                    type="button"
                                    disabled={busy || !!cropRegion}
                                    onClick={() => inputsRef.current[name]?.click()}
                                    data-testid={`asbuilt-region-${name}-upload`}
                                    className="absolute top-2 right-2 z-20 inline-flex items-center justify-center w-7 h-7 rounded bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white shadow disabled:opacity-60"
                                    title={filled ? "Replace image" : "Upload image"}
                                    aria-label={filled ? "Replace image" : "Upload image"}
                                >
                                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (filled ? <Camera className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />)}
                                </button>
                            )}

                            {/* Actions */}
                            <div className="absolute bottom-2 right-2 flex items-center gap-1.5 z-20 flex-wrap justify-end">
                                {filled && !inCrop && (
                                    <button
                                        type="button"
                                        disabled={busy || !!cropRegion}
                                        onClick={() => startCrop(name)}
                                        data-testid={`asbuilt-region-${name}-crop`}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white text-[11px] font-bold shadow disabled:opacity-40"
                                        title="Drag to reposition or resize inside the box"
                                    >
                                        <Move className="w-3 h-3" /> Adjust
                                    </button>
                                )}
                                {inCrop && (
                                    <>
                                        <button type="button" onClick={() => bumpZoom(-0.1)} className="inline-flex items-center px-1.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white shadow" title="Zoom out">
                                            <ZoomOut className="w-3 h-3" />
                                        </button>
                                        <button type="button" onClick={() => bumpZoom(0.1)} className="inline-flex items-center px-1.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white shadow" title="Zoom in">
                                            <ZoomIn className="w-3 h-3" />
                                        </button>
                                        <button type="button" onClick={resetCrop} className="inline-flex items-center px-1.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white shadow" title="Reset">
                                            <RotateCcw className="w-3 h-3" />
                                        </button>
                                        <button type="button" onClick={cancelCrop} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-bold shadow" data-testid={`asbuilt-region-${name}-crop-cancel`}>
                                            Cancel
                                        </button>
                                        <button type="button" onClick={saveCrop} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold shadow" data-testid={`asbuilt-region-${name}-crop-save`}>
                                            <Check className="w-3 h-3" /> Done
                                        </button>
                                    </>
                                )}
                                {filled && !inCrop && (
                                    <button
                                        type="button"
                                        disabled={busy || !!cropRegion}
                                        onClick={() => deleteRegion(name)}
                                        data-testid={`asbuilt-region-${name}-delete`}
                                        className="inline-flex items-center px-2 py-1 rounded bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-bold shadow disabled:opacity-60"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                            <input
                                ref={(el) => { inputsRef.current[name] = el; }}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    if (f) uploadRegion(name, f);
                                }}
                                data-testid={`asbuilt-region-${name}-input`}
                            />
                        </div>
                    );
                })}

                {/* Elevation values live INSIDE the template's baked
                    "ELEVATIONS: SEPTIC SYSTEM" box (right title strip).
                    Inputs are pixel-mapped to the label rows; the
                    server stamps the same values there on compose. */}
                {ELEVATION_ROWS.map((row) => (
                    <input
                        key={row.key}
                        aria-label={row.label}
                        data-testid={`asbuilt-elev-${row.key}`}
                        value={elevDisplay[row.key] || ""}
                        onChange={(e) => setElev((v) => ({ ...v, [row.key]: e.target.value }))}
                        onBlur={saveElevations}
                        onMouseDown={(e) => e.stopPropagation()}
                        placeholder="—"
                        className="absolute z-10 bg-transparent text-right text-slate-900 font-bold tabular-nums leading-none px-[0.2cqw] rounded-sm border border-transparent hover:border-[#1a3fd1]/50 focus:outline-none focus:border-[#1a3fd1]/50 focus:bg-white"
                        style={{
                            left: ELEV_VALUE_BOX.left, width: ELEV_VALUE_BOX.width,
                            top: row.top, height: ELEV_VALUE_BOX.height,
                            fontSize: "0.85cqw",
                        }}
                    />
                ))}
                {ocrBusy && (
                    <div
                        className="absolute z-10 flex items-center gap-1 text-[#8fa4f0] font-bold"
                        style={{ left: "80.5%", top: "35.5%", fontSize: "0.8cqw" }}
                        data-testid="asbuilt-ocr-busy"
                    >
                        <Loader2 className="w-[1cqw] h-[1cqw] animate-spin" /> Reading elevations…
                    </div>
                )}
            </div>
                </div>
            </div>

            {/* iter-168d — Email dialog */}
            {emailOpen && (
                <div className="fixed inset-0 bg-[#111] z-50 flex items-center justify-center p-4" onClick={() => !emailBusy && setEmailOpen(false)} data-testid="asbuilt-email-dialog">
                    <div className="bg-white rounded shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
                            <div className="text-sm font-bold text-slate-900">Email As-Built</div>
                            <button type="button" onClick={() => !emailBusy && setEmailOpen(false)} className="text-slate-500 hover:text-slate-800">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4 flex flex-col gap-3 text-xs text-slate-900">
                            <label className="flex flex-col gap-1">
                                <span className="font-bold uppercase tracking-wider text-[10px] text-slate-600">From</span>
                                <select
                                    value={emailForm.account_id}
                                    onChange={(e) => setEmailForm((f) => ({ ...f, account_id: e.target.value }))}
                                    className="border border-slate-300 rounded px-2 py-1.5 text-slate-900"
                                    data-testid="asbuilt-email-from"
                                >
                                    {accounts.length === 0 && <option value="">— No email accounts connected —</option>}
                                    {accounts.map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {a.display_name ? `${a.display_name} <${a.email_address}>` : a.email_address}
                                        </option>
                                    ))}
                                </select>
                                {accounts.length === 0 && (
                                    <span className="text-[10px] text-rose-600">Connect one in Email → Settings first.</span>
                                )}
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="font-bold uppercase tracking-wider text-[10px] text-slate-600 flex items-center justify-between">
                                    <span>To</span>
                                    {linkedContact?.email && <span className="text-emerald-600 font-normal">Auto-filled from linked contact</span>}
                                </span>
                                <input
                                    value={emailForm.to}
                                    onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))}
                                    placeholder="name@example.com"
                                    className="border border-slate-300 rounded px-2 py-1.5 text-slate-900"
                                    data-testid="asbuilt-email-to"
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="font-bold uppercase tracking-wider text-[10px] text-slate-600">Subject</span>
                                <input
                                    value={emailForm.subject}
                                    onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
                                    className="border border-slate-300 rounded px-2 py-1.5 text-slate-900"
                                    data-testid="asbuilt-email-subject"
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="font-bold uppercase tracking-wider text-[10px] text-slate-600">Message</span>
                                <textarea
                                    value={emailForm.body}
                                    onChange={(e) => setEmailForm((f) => ({ ...f, body: e.target.value }))}
                                    rows={5}
                                    className="border border-slate-300 rounded px-2 py-1.5 text-slate-900 resize-vertical"
                                    data-testid="asbuilt-email-body"
                                />
                            </label>
                            <div className="text-[10px] text-slate-500">
                                The current as-built (with your latest crops + elevations) is attached automatically.
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-2">
                            <button type="button" onClick={() => setEmailOpen(false)} disabled={emailBusy} className="text-xs font-bold px-3 py-1.5 rounded text-slate-700 hover:bg-slate-100">Cancel</button>
                            <button type="button" onClick={sendEmail} disabled={emailBusy || !emailForm.account_id || !emailForm.to} data-testid="asbuilt-email-send" className="text-xs font-bold px-3 py-1.5 rounded bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white shadow disabled:opacity-60 inline-flex items-center gap-1.5">
                                {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
